/**
 * Right-rail chat for the workflow shell.
 *
 * The CM Test-Graph iframe is cross-origin so the parent SPA cannot type
 * directly into its input. We bridge that gap with postMessage:
 *
 *   1. Render the case context as plain text in a preview block, with a
 *      "Send to chatbox" button. Clicking it posts a
 *      ``{ type: 'cm:fill-input', text }`` message to the iframe. The
 *      embedded ``TestGraphChat`` listens for that event and drops the
 *      text into its input textarea (user reviews + presses Enter).
 *      Clipboard write is still done as a defensive fallback so the user
 *      can manually paste if the iframe handler is ever unavailable.
 *   2. Append a ``seed=<encoded>`` query-string parameter to the iframe
 *      URL so a fresh iframe load can also pre-fill (no-op until the
 *      embed adds explicit seed handling).
 *   3. Best-effort ``cm:case-context`` postMessage on mount so the chat
 *      can show inline case banners if/when it wants to. The current
 *      embed treats this passively (no auto-fill of the input).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getCmTestGraphUrl } from '../../utils/env';
import { Icon } from './WorkflowIcons';

function buildSeed(context) {
  if (!context) return '';
  try {
    return encodeURIComponent(String(context).slice(0, 4000));
  } catch {
    return '';
  }
}

function buildSrc(seed) {
  const base = getCmTestGraphUrl();
  if (!seed) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}seed=${seed}`;
}

export default function CaseChatDrawer({
  caseContext = '',
  title = 'Ask about this case',
  sub = 'Chat is grounded in the denial-mitigation knowledge graph (CARC/RARC, NCCI/MUE, payer policies, LCD/NCD, appeals).',
  emptyHint = 'Run a case above to populate the chat context.',
}) {
  const iframeRef = useRef(null);
  const [sent, setSent] = useState(false);
  const seed = useMemo(() => buildSeed(caseContext), [caseContext]);
  const src = useMemo(() => buildSrc(seed), [seed]);

  useEffect(() => {
    if (!caseContext) return;
    const t = setTimeout(() => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'cm:case-context', context: caseContext },
          '*',
        );
      } catch { /* ignore */ }
    }, 800);
    return () => clearTimeout(t);
  }, [caseContext, src]);

  // Send the case context straight into the iframe's chat input textarea
  // via postMessage ('cm:fill-input'). Fall back to clipboard so the user
  // can manually paste if the iframe handler is ever missing.
  const onSendToChat = async () => {
    if (!caseContext) return;
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'cm:fill-input', text: caseContext },
        '*',
      );
    } catch { /* ignore */ }
    try {
      await navigator.clipboard.writeText(caseContext);
    } catch { /* ignore */ }
    setSent(true);
    setTimeout(() => setSent(false), 1800);
  };

  return (
    <aside className="wf-rail" aria-label="Case chat">
      <div className="wf-rail__head">
        <h3 className="wf-rail__title">
          <Icon.chat /> {title}
        </h3>
        <a className="wf-btn wf-btn--ghost" href={src} target="_blank" rel="noreferrer" style={{ padding: '5px 9px', fontSize: 12 }}>
          Open
        </a>
      </div>
      <p className="wf-rail__sub">{sub}</p>

      {caseContext ? (
        <>
          <div className="wf-rail__context" aria-label="Case context to send into chat">
            {caseContext}
          </div>
          <div className="wf-actions" style={{ alignItems: 'center' }}>
            <button type="button" className="wf-btn wf-btn--ghost" onClick={onSendToChat}>
              <Icon.send /> Send to chatbox
            </button>
            {sent && <span className="wf-rail__copied">Sent to chatbox below</span>}
          </div>
        </>
      ) : (
        <div className="wf-rail__empty">{emptyHint}</div>
      )}

      <div className="wf-rail__frame-wrap">
        <iframe
          ref={iframeRef}
          src={src}
          title="Case chat"
          className="wf-rail__frame"
          allow="clipboard-write; clipboard-read"
        />
      </div>
    </aside>
  );
}
